import { useCallback, useState } from 'react';

/**
 * 엑셀 내보내기 모달의 "항목(컬럼) 선택" 영속 훅 (2026-06-20).
 *
 * 어떤 컬럼을 내보낼지는 서버 데이터가 아니라 운영자 개인의 화면 환경설정이므로
 * **클라이언트 전용(localStorage)** 으로 보관한다(BE 저장 불필요). 모달 재오픈/세션 간
 * 직전 선택을 유지하고, 첫 사용(저장값 없음)은 기본값(전체 선택)으로 시작한다.
 * 선례: stores/useProductColumnPrefs.ts(목록 노출 컬럼)와 동일한 방어적 read 패턴.
 *
 * 영속 스키마 `{ selected, known }`:
 *  - `selected`: 운영자가 선택한 컬럼 키.
 *  - `known`: 저장 시점에 존재했던 전체 컬럼 키(스키마 스냅샷).
 * read 시 `allKeys` 중 저장된 `known` 에 **없던** 키(=이후 추가된 신규 컬럼)는 기본 포함한다.
 * `known` 에는 있었지만 `selected` 에 없는 키는 운영자가 의도적으로 끈 것이므로 제외 유지한다.
 * 이로써 "신규 컬럼 추가 시 기본 포함 + 기존 해제 선택은 보존" 둘 다 만족한다.
 *
 * 모달별 localStorage 키는 호출측이 `storageKey` 로 구분한다(예 'order-export-columns').
 */

interface StoredSelection {
  selected: string[];
  known: string[];
}

const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === 'string');

/**
 * 저장값과 현재 컬럼 집합을 병합해 유효한 선택을 도출한다.
 * - 저장값 없음/형식 깨짐 → 기본값(defaultKeys, allKeys 순서 보존).
 * - 신규 컬럼(allKeys 에 있으나 저장 known 에 없음) → 기본 포함.
 * - 기존 해제(known 에 있었으나 selected 에 없음) → 제외 유지.
 * - 알 수 없는 잔존 키(allKeys 에 없음) → 무시.
 * 반환은 항상 allKeys 순서로 정렬해 컬럼 순서를 안정화한다.
 */
const resolveSelection = (
  raw: string | null,
  allKeys: ReadonlyArray<string>,
  defaultKeys: ReadonlyArray<string>,
): ReadonlyArray<string> => {
  const orderBy = (keys: ReadonlyArray<string>): string[] => {
    const set = new Set(keys);
    return allKeys.filter((k) => set.has(k));
  };
  if (!raw) return orderBy(defaultKeys);
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !isStringArray((parsed as StoredSelection).selected) ||
      !isStringArray((parsed as StoredSelection).known)
    ) {
      return orderBy(defaultKeys);
    }
    const { selected, known } = parsed as StoredSelection;
    const selectedSet = new Set(selected);
    const knownSet = new Set(known);
    // allKeys 를 훑어: 저장 known 에 있던 키는 selected 여부 따름, 신규 키(known 미포함)는 기본 포함.
    return allKeys.filter((k) => (knownSet.has(k) ? selectedSet.has(k) : true));
  } catch {
    return orderBy(defaultKeys);
  }
};

const persist = (
  storageKey: string,
  selected: ReadonlyArray<string>,
  allKeys: ReadonlyArray<string>,
): void => {
  if (typeof window === 'undefined') return;
  try {
    const payload: StoredSelection = { selected: [...selected], known: [...allKeys] };
    window.localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch {
    // localStorage 불가(프라이빗 모드/할당량) — 화면 동작은 유지하고 영속만 생략.
  }
};

const readInitial = (
  storageKey: string,
  allKeys: ReadonlyArray<string>,
  defaultKeys: ReadonlyArray<string>,
): ReadonlyArray<string> => {
  if (typeof window === 'undefined') {
    const set = new Set(defaultKeys);
    return allKeys.filter((k) => set.has(k));
  }
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(storageKey);
  } catch {
    raw = null;
  }
  return resolveSelection(raw, allKeys, defaultKeys);
};

export interface ExportColumnSelection {
  /** 현재 선택된 컬럼 키(allKeys 순서). */
  columns: ReadonlyArray<string>;
  /** 단건 토글(체크/해제) — localStorage 영속 반영. */
  toggleColumn: (key: string, checked: boolean) => void;
  /** 전체 선택(allKeys) — 영속 반영. */
  selectAll: () => void;
  /** 전체 해제(빈 선택) — 영속 반영. */
  clearAll: () => void;
}

/**
 * 엑셀 export 컬럼 선택을 localStorage 에 영속하는 훅.
 *
 * @param storageKey 모달별 고유 localStorage 키(예 'order-export-columns').
 * @param allKeys    선택 가능한 전체 컬럼 키(정본 순서).
 * @param defaultKeys 저장값이 없을 때 기본 선택(미지정=전체 선택).
 */
export const useExportColumnSelection = (
  storageKey: string,
  allKeys: ReadonlyArray<string>,
  defaultKeys: ReadonlyArray<string> = allKeys,
): ExportColumnSelection => {
  const [columns, setColumns] = useState<ReadonlyArray<string>>(() =>
    readInitial(storageKey, allKeys, defaultKeys),
  );

  const apply = useCallback(
    (next: ReadonlyArray<string>) => {
      persist(storageKey, next, allKeys);
      setColumns(next);
    },
    [storageKey, allKeys],
  );

  const toggleColumn = useCallback(
    (key: string, checked: boolean) => {
      setColumns((prev) => {
        const has = prev.includes(key);
        if (checked === has) return prev;
        const nextSet = new Set(prev);
        if (checked) nextSet.add(key);
        else nextSet.delete(key);
        // allKeys 순서로 정규화해 컬럼 순서를 안정화.
        const next = allKeys.filter((k) => nextSet.has(k));
        persist(storageKey, next, allKeys);
        return next;
      });
    },
    [storageKey, allKeys],
  );

  const selectAll = useCallback(() => apply([...allKeys]), [apply, allKeys]);
  const clearAll = useCallback(() => apply([]), [apply]);

  return { columns, toggleColumn, selectAll, clearAll };
};
